<!-- BaseFileDropAttach.vue (client-lms) -->
<!-- 쪽지 등에서 사용하는 파일 첨부 컴포넌트 (드래그앤드롭 + 리스트형) -->
<template>
  <div class="lms_fda">
    <!-- 상단: 라벨 + 파일선택 버튼 + 용량 안내 -->
    <div class="lms_fda_header">
      <span class="lms_fda_label">첨부파일</span>
      <!-- label 이 input 을 감싸므로 native 클릭으로 파일창 오픈 (JS 수동 click 시 버블링된 click 에
           @click.prevent 가 다시 걸려 다이얼로그가 취소됨). maxFiles 초과는 disabled+pointer-events:none 로 차단. -->
      <label class="lms_fda_btn" :class="{ disabled: files.length >= maxFiles }">
        <i class="icon_file_up"></i>
        파일 선택
        <input
          ref="fileInput"
          type="file"
          :accept="accept"
          :multiple="multiple"
          hidden
          @change="onFileSelect"
        />
      </label>
    </div>

    <!-- 파일 리스트 -->
    <div v-if="files.length > 0" class="lms_fda_list">
      <div v-for="(file, idx) in files" :key="idx" class="lms_fda_file">
        <i class="icon_file_download"></i>
        <span class="lms_fda_file_name">{{ file.name }}</span>
        <span class="lms_fda_file_size">{{ formatSize(file.size) }}</span>
        <button type="button" class="lms_fda_file_remove" @click="removeFile(idx)">
          <i class="mdi mdi-close"></i>
        </button>
      </div>
    </div>

    <!-- 드롭존 -->
    <div
      v-if="files.length === 0"
      class="lms_fda_dropzone"
      :class="{ active: isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave="isDragging = false"
      @drop.prevent="onDrop"
      @click="fileInput?.click()"
    >
      <span class="lms_fda_drop_text">{{ dropMessage }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

const props = defineProps({
  accept: { type: String, default: "" },
  multiple: { type: Boolean, default: true },
  maxFiles: { type: Number, default: 5 },
  maxSize: { type: Number, default: 10 }, // MB
  dropMessage: {
    type: String,
    default: "업로드 버튼을 클릭하거나 파일을 끌어서 첨부파일을 업로드해 주세요.",
  },
});

const files = defineModel<File[]>({ default: () => [] });
const fileInput = ref<HTMLInputElement>();
const isDragging = ref(false);

const maxSizeBytes = props.maxSize * 1024 * 1024;

/* ── File Select ── */
const onFileSelect = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (!input.files) return;
  addFiles(input.files);
  input.value = "";
};

/* ── Drag & Drop ── */
const onDrop = (e: DragEvent) => {
  isDragging.value = false;
  if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
};

/* ── Add Files ── */
const addFiles = (fileList: FileList) => {
  for (const file of Array.from(fileList)) {
    if (files.value.length >= props.maxFiles) break;
    if (file.size > maxSizeBytes) continue;
    // 중복 체크
    if (files.value.some((f) => f.name === file.name && f.size === file.size)) continue;
    files.value.push(file);
  }
};

/* ── Remove ── */
const removeFile = (idx: number) => {
  files.value.splice(idx, 1);
};

/* ── Helpers ── */
const formatSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + "B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + "KB";
  return (bytes / (1024 * 1024)).toFixed(1) + "MB";
};


defineExpose({ trigger: () => fileInput.value?.click() });
</script>

<style scoped>
.lms_fda {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Header ── */
.lms_fda_header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.lms_fda_header .icon_file_up {
  background-color: #fff !important;
}

.lms_fda_label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}

.lms_fda_btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 16px;
  border: 1px solid var(--lms-outline-gray-main, #d1d5db);
  border-radius: 6px;
  font-family: "Pretendard", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  transition: background 0.15s;
}
.lms_fda_btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.lms_fda_btn i {
  font-size: 17px;
  color: #fff;
}

.lms_fda_list {
  height: 88px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 8px 18px;
  margin-bottom: 12px ;
  border: 1px solid #d9dce0;
  border-radius: 3px;
  background: #fff;
}

.lms_fda_file {
  height: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border-radius: 0;
}

.lms_fda_file:hover {
  background: transparent;
}

.lms_fda_file > .mdi {
  font-size: 16px;
  color: #8a8f94;
}

.lms_fda_file_name {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
  line-height: 30px;
}

.lms_fda_file_size {
  display: none;
}
/* 첨부파일 개별 삭제(×) 버튼 — 추가한 파일을 다시 제거 */
.lms_fda_file_remove {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #8a8f94;
  cursor: pointer;
  transition:
    color 0.15s,
    background 0.15s;
}
.lms_fda_file_remove .mdi {
  font-size: 15px;
}
.lms_fda_file_remove:hover {
  color: #ef4444;
  background: #fef2f2;
}

/* ── Dropzone ── */
.lms_fda_dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 30px;
  padding: 12px 16px;
  border: 1px dashed var(--lms-outline-gray-main, #d1d5db);
  border-radius: 6px;
  background: #fafafa;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;
}

.lms_fda_dropzone:hover {
  border-color: #9ca3af;
  background: #f3f4f6;
}

.lms_fda_dropzone.active {
  border-color: var(--lms-blue-400, #5c74f6);
  background: #eef0fe;
}

.lms_fda_drop_text {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.35);
  text-align: center;
  line-height: 1.4;
  white-space: pre-line;
}
</style>
